import { useState, useEffect, useCallback } from 'react';
import { formatUiDate } from '../utils/uiFormat';
import {
  getBusinessBoundaryTimes,
  resolveStandardStartEdit,
  shiftDateRangeByPreset,
  shouldKeepStandardPresetForEnd,
} from '../utils/dateRangeUi';

// Format date to YYYY-MM-DD
const formatDateValue = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

// Get business date based on business day start hour
const getBusinessDate = (businessDayStartHour = 6) => {
  const now = new Date();
  const businessNow = new Date(now);
  if (now.getHours() < businessDayStartHour) {
    businessNow.setDate(businessNow.getDate() - 1);
  }
  return businessNow;
};

const formatDateTimeDisplay = (date, time) => {
  if (!date || !time) return '';
  return `${formatUiDate(date)} ${String(time).substring(0, 5)}`;
};

// Get end time based on start hour (1 minute before start)
const getEndTimeFromStart = (startHour) => {
  const endHour = startHour === 0 ? 23 : startHour - 1;
  return String(endHour).padStart(2, '0') + ':59';
};

const PRESETS = {
  today: { label: 'Today', icon: 'Calendar' },
  week: { label: 'This Week', icon: 'CalendarDays' },
  month: { label: 'This Month', icon: 'CalendarDays' },
  year: { label: 'This Year', icon: 'CalendarDays' },
  custom: { label: 'Custom', icon: 'Clock' },
};

const getInitialStartDate = (preset, businessDayStartHour) => {
  const today = getBusinessDate(businessDayStartHour);
  if (preset === 'month') return formatDateValue(new Date(today.getFullYear(), today.getMonth(), 1));
  if (preset === 'year') return formatDateValue(new Date(today.getFullYear(), 0, 1));
  if (preset === 'week') {
    const day = today.getDay();
    const diff = day === 0 ? 6 : day - 1;
    const start = new Date(today);
    start.setDate(today.getDate() - diff);
    return formatDateValue(start);
  }
  return formatDateValue(today);
};

const getInitialEndDate = (preset, businessDayStartHour) => {
  const today = getBusinessDate(businessDayStartHour);
  if (preset === 'week') {
    const day = today.getDay();
    const diff = day === 0 ? 6 : day - 1;
    const end = new Date(today);
    end.setDate(today.getDate() - diff + 6);
    return formatDateValue(end);
  }
  if (preset === 'month') return formatDateValue(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  if (preset === 'year') return formatDateValue(new Date(today.getFullYear(), 11, 31));
  return formatDateValue(today);
};

export const useDateFilter = (initialPreset = 'today', businessDayStartHour = 6) => {
  const [preset, setPresetState] = useState(initialPreset);
  const [startDate, setStartDateState] = useState(() => getInitialStartDate(initialPreset, businessDayStartHour));
  const [endDate, setEndDateState] = useState(() => getInitialEndDate(initialPreset, businessDayStartHour));
  const [startTime, setStartTimeState] = useState(String(businessDayStartHour).padStart(2, '0') + ':00');
  const [endTime, setEndTimeState] = useState(getEndTimeFromStart(businessDayStartHour));

  // Update times when business day start hour changes. Preset changes are handled
  // by applyPreset or by explicit page-level period pickers.
  useEffect(() => {
    setStartTimeState(String(businessDayStartHour).padStart(2, '0') + ':00');
    setEndTimeState(getEndTimeFromStart(businessDayStartHour));
    if (preset !== 'custom') {
      setStartDateState(getInitialStartDate(preset, businessDayStartHour));
      setEndDateState(getInitialEndDate(preset, businessDayStartHour));
    }
  }, [businessDayStartHour]);

  const applyPreset = useCallback((newPreset) => {
    setPresetState(newPreset);
    if (newPreset !== 'custom') {
      setStartTimeState(String(businessDayStartHour).padStart(2, '0') + ':00');
      setEndTimeState(getEndTimeFromStart(businessDayStartHour));
    }
    const today = getBusinessDate(businessDayStartHour);
    let start;
    
    switch (newPreset) {
      case 'today':
        start = new Date(today);
        setStartDateState(formatDateValue(start));
        setEndDateState(formatDateValue(today));
        break;
      case 'week':
        const day = today.getDay();
        const diff = day === 0 ? 6 : day - 1; // Monday is first day
        start = new Date(today);
        start.setDate(today.getDate() - diff);
        const endOfWeek = new Date(start);
        endOfWeek.setDate(start.getDate() + 6);
        setStartDateState(formatDateValue(start));
        setEndDateState(formatDateValue(endOfWeek));
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        setStartDateState(formatDateValue(start));
        setEndDateState(formatDateValue(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        setStartDateState(formatDateValue(start));
        setEndDateState(formatDateValue(new Date(today.getFullYear(), 11, 31)));
        break;
      case 'custom':
        // Don't change dates for custom
        break;
      default:
        break;
    }
  }, [businessDayStartHour]);

  const setStartDate = useCallback((value) => {
    const range = resolveStandardStartEdit({
      preset,
      nextStartDate: value,
      startTime,
      endTime,
      businessDayStartHour,
    });
    if (range) {
      setStartDateState(range.startDate);
      setEndDateState(range.endDate);
      return;
    }
    setPresetState('custom');
    setStartDateState(value);
  }, [businessDayStartHour, endTime, preset, startTime]);

  const setEndDate = useCallback((value) => {
    if (!shouldKeepStandardPresetForEnd({
      preset,
      startDate,
      nextEndDate: value,
      startTime,
      endTime,
      businessDayStartHour,
    })) {
      setPresetState('custom');
    }
    setEndDateState(value);
  }, [businessDayStartHour, endTime, preset, startDate, startTime]);

  const setStartTime = useCallback((value) => {
    const boundary = getBusinessBoundaryTimes(businessDayStartHour);
    if (preset !== 'custom' && (value !== boundary.startTime || endTime !== boundary.endTime)) {
      setPresetState('custom');
    }
    setStartTimeState(value);
  }, [businessDayStartHour, endTime, preset]);

  const setEndTime = useCallback((value) => {
    const boundary = getBusinessBoundaryTimes(businessDayStartHour);
    if (preset !== 'custom' && (startTime !== boundary.startTime || value !== boundary.endTime)) {
      setPresetState('custom');
    }
    setEndTimeState(value);
  }, [businessDayStartHour, preset, startTime]);

  const setCustomRange = useCallback(({
    startDate: nextStartDate,
    endDate: nextEndDate,
    startTime: nextStartTime,
    endTime: nextEndTime,
  }) => {
    setPresetState('custom');
    setStartDateState(nextStartDate);
    setEndDateState(nextEndDate);
    if (nextStartTime != null) setStartTimeState(nextStartTime);
    if (nextEndTime != null) setEndTimeState(nextEndTime);
  }, []);

  const shiftRange = useCallback((delta) => {
    const shifted = shiftDateRangeByPreset({
      preset,
      startDate,
      endDate,
      delta,
    });
    setStartDateState(shifted.startDate);
    setEndDateState(shifted.endDate);
  }, [endDate, preset, startDate]);

  // Get the next calendar day of a date string (business days end on next calendar day)
  const getNextCalendarDay = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + 1);
    return formatDateValue(d);
  };

  // Get {from, to} strings with datetime for Period card display
  const getPeriodParts = useCallback(() => {
    const startHrStr = String(businessDayStartHour).padStart(2, '0') + ':00';
    const endDisplayDate = getNextCalendarDay(endDate);
    const fromStr = preset === 'custom'
      ? formatDateTimeDisplay(startDate, startTime)
      : formatDateTimeDisplay(startDate, startHrStr);
    const toStr = formatDateTimeDisplay(endDisplayDate, endTime);
    return { from: fromStr, to: toStr };
  }, [preset, startDate, endDate, startTime, endTime, businessDayStartHour]);

  // Get display text for the current date range
  // Business day always ends on the NEXT calendar day at endTime (e.g. 05:59)
  const getDateRangeDisplay = useCallback(() => {
    const startHrStr = String(businessDayStartHour).padStart(2, '0') + ':00';
    const endDisplayDate = getNextCalendarDay(endDate);

    if (preset === 'custom') {
      const startDT = formatDateTimeDisplay(startDate, startTime);
      const endDT = formatDateTimeDisplay(endDisplayDate, endTime);
      return `${startDT} ... ${endDT}`;
    }

    const startDT = formatDateTimeDisplay(startDate, startHrStr);
    const endDT = formatDateTimeDisplay(endDisplayDate, endTime);
    return `${startDT} ... ${endDT}`;
  }, [preset, startDate, endDate, startTime, endTime, businessDayStartHour]);

  // Build URL params for API calls
  const buildUrlParams = useCallback((includeTime = true) => {
    let params = `start_date=${startDate}&end_date=${endDate}`;
    if (includeTime && preset === 'custom') {
      params += `&start_time=${startTime}&end_time=${endTime}`;
    }
    return params;
  }, [startDate, endDate, startTime, endTime, preset]);

  // Get backend-compatible date filter params (for orders endpoint)
  const getBackendParams = useCallback(() => {
    switch (preset) {
      case 'today':
        return 'today=true';
      case 'week':
      case 'month':
      case 'year':
        return `start_date=${startDate}&end_date=${endDate}`;
      case 'custom':
        let params = `start_date=${startDate}&end_date=${endDate}`;
        if (startTime) params += `&start_time=${startTime}`;
        if (endTime) params += `&end_time=${endTime}`;
        return params;
      default:
        return `start_date=${startDate}&end_date=${endDate}`;
    }
  }, [preset, startDate, endDate, startTime, endTime]);

  return {
    preset,
    setPreset: setPresetState,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    setCustomRange,
    shiftRange,
    applyPreset,
    getDateRangeDisplay,
    getPeriodParts,
    buildUrlParams,
    getBackendParams,
    PRESETS,
  };
};

export default useDateFilter;
